import { merge, round } from 'lodash';
import { useCallback, useContext, useMemo } from 'react';
import { StyleSheet } from 'react-native';
import { MBButton } from '@shared/components/MBButton';
import { MBView } from '@shared/components/MBView';
import { View } from '@shared/components/View';
import { MBTouchableOpacity } from '@shared/components/touchable';
import {
  MBTextBody1Bold,
  MBTextBody2Bold,
  MBTextCaption1Bold,
  MBTextOverlineBold,
} from '@shared/components/typography';
import {
  ColorThemeContext,
  Colors,
  PRICING_LIMIT_HISTORY_DAYS,
  SharedStyles,
  defaultTheme,
  grid,
} from '@shared/styles';
import { i18n } from '@shared/util/i18n';
import { MBLinking } from '@shared/util/linking';

interface Props {
  messages: number;
  messageLimit: number;
  storage: number;
  storageLimit: number;
  cards: number;
  cardLimit: number;
  onUpgrade(): void;
}

interface RowProps {
  label: string;
  progress: number;
  limitReached: boolean;
}

function Row({ label, progress, limitReached }: RowProps) {
  const colors = useContext(ColorThemeContext);
  return (
    <View paddingBottom={2}>
      <MBTextOverlineBold color={colors.foregroundActive}>
        {label?.toUpperCase() ?? ''}
      </MBTextOverlineBold>
      {!limitReached && (
        <MBView style={[styles.progressBarContainer, { backgroundColor: colors.background }]}>
          <MBView
            style={[
              styles.progressBarLine,
              {
                backgroundColor: colors.accent,
                width: `${Math.min(progress, 1) * 100}%`,
              },
            ]}
          />
        </MBView>
      )}
    </View>
  );
}

export function bytesToGB(bytes: number, numDecimals: number = 0): number {
  return round(bytes / Math.pow(1024, 3), numDecimals);
}

const limitReachedTheme = merge({}, defaultTheme, {
  accent: Colors.v2.actionTropicalRed,
  highlightInactive: Colors.v2.actionTropicalRed10,
});

export function MBMetricsCounterComponent({
  messages,
  messageLimit,
  storage,
  storageLimit,
  cards,
  cardLimit,
  onUpgrade,
}: Props) {
  const limitReached = useMemo(() => {
    return messages > messageLimit || storage > storageLimit || cards > cardLimit;
  }, [messages, messageLimit, storage, storageLimit, cards, cardLimit]);

  const openLearnMore = useCallback(
    () => MBLinking.open(import.meta.env.VITE_PRICING_LEARN_MORE_URL),
    [],
  );

  return (
    <ColorThemeContext.Provider value={limitReached ? limitReachedTheme : defaultTheme}>
      <ColorThemeContext.Consumer>
        {colors => (
          <MBView
            paddingHorizontal={3}
            paddingVertical={2}
            style={[
              {
                backgroundColor: colors.highlightInactive,
                borderColor: colors.accent,
                borderWidth: 1,
              },
              SharedStyles.borderRadius,
            ]}
          >
            <MBTextBody1Bold color={colors.foregroundActive}>
              {i18n('billing.metricsCounter.title')}
            </MBTextBody1Bold>
            <View paddingBottom={1} />
            {limitReached && (
              <View paddingBottom={2}>
                <MBTextBody2Bold color={colors.accent}>
                  {i18n('billing.metricsCounter.limitReached.description', {
                    numDays: PRICING_LIMIT_HISTORY_DAYS,
                  })}
                </MBTextBody2Bold>
              </View>
            )}
            <Row
              label={i18n(
                limitReached
                  ? 'billing.metricsCounter.limitReached.messages'
                  : 'billing.metricsCounter.limitNotReached.messages',
                {
                  count: round(messages / 1000, 2),
                  max: round(messageLimit / 1000, 2),
                },
              )}
              progress={messages / messageLimit}
              limitReached={limitReached}
            />
            <Row
              label={i18n(
                limitReached
                  ? 'billing.metricsCounter.limitReached.cards'
                  : 'billing.metricsCounter.limitNotReached.cards',
                { count: cards, max: cardLimit },
              )}
              progress={cards / cardLimit}
              limitReached={limitReached}
            />
            <Row
              label={i18n(
                limitReached
                  ? 'billing.metricsCounter.limitReached.storage'
                  : 'billing.metricsCounter.limitNotReached.storage',
                {
                  count: bytesToGB(storage, 2),
                  max: bytesToGB(storageLimit, 2),
                },
              )}
              progress={storage / storageLimit}
              limitReached={limitReached}
            />

            <MBTouchableOpacity onPress={openLearnMore}>
              <MBTextCaption1Bold color={colors.accent}>
                {i18n('billing.metricsCounter.learnMore')}
              </MBTextCaption1Bold>
            </MBTouchableOpacity>
            {limitReached && (
              <>
                <View paddingBottom={3} />
                <MBButton
                  title={i18n('billing.metricsCounter.upgrade')}
                  size="small"
                  onPress={onUpgrade}
                />
              </>
            )}
          </MBView>
        )}
      </ColorThemeContext.Consumer>
    </ColorThemeContext.Provider>
  );
}

const styles = StyleSheet.create({
  progressBarContainer: {
    marginTop: grid(1),
    height: 2,
    width: '100%',
  },
  progressBarLine: {
    height: '100%',
  },
});
